import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface MarketplaceSyncPopconfirmProps {
  /** Extra class(es) appended to the base class (e.g. the `--stacked` variant). */
  className?: string;
  /**
   * Requested when the popconfirm must close on its own: any scroll (the
   * fixed-positioned portal cannot follow its anchor) or a window resize.
   */
  onRequestClose: () => void;
  children: React.ReactNode;
}

/** Above `.modal-overlay` (z-index 1000) so the popconfirm is never covered
 * or clipped by a modal / its scrollable `.modal-body`. */
const POPCONFIRM_Z_INDEX = 1100;

/**
 * The `.marketplace-sync-popconfirm` bubble, rendered through a portal to
 * `document.body` with `position: fixed` - anchored above / right-aligned to
 * its parent element (the `.marketplace-sync-popconfirm-anchor` wrapper it is
 * rendered inside of). Escapes the modal stacking context and the scrollable
 * modal body that used to clip the previously absolute-positioned bubble.
 * Because a fixed element cannot follow the anchor while a container scrolls,
 * any scroll simply closes the popconfirm (`onRequestClose`).
 */
const MarketplaceSyncPopconfirm: React.FC<MarketplaceSyncPopconfirmProps> = ({
  className,
  onRequestClose,
  children,
}) => {
  /** Invisible in-place probe - its parent is the anchor to align to. */
  const probeRef = useRef<HTMLSpanElement>(null);
  const [position, setPosition] = useState<{ bottom: number; right: number } | null>(null);

  useLayoutEffect(() => {
    const anchor = probeRef.current?.parentElement;
    if (!anchor) {
      return;
    }
    const rect = anchor.getBoundingClientRect();
    setPosition({
      bottom: window.innerHeight - rect.top + 8,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useEffect(() => {
    // Capture phase catches scrolls of ANY container (modal body, the
    // category picker list, the page) - not just the window.
    window.addEventListener('scroll', onRequestClose, true);
    window.addEventListener('resize', onRequestClose);
    return () => {
      window.removeEventListener('scroll', onRequestClose, true);
      window.removeEventListener('resize', onRequestClose);
    };
  }, [onRequestClose]);

  return (
    <span ref={probeRef}>
      {position
        ? createPortal(
            <span
              className={`marketplace-sync-popconfirm${className ? ` ${className}` : ''}`}
              style={{
                position: 'fixed',
                bottom: position.bottom,
                right: position.right,
                zIndex: POPCONFIRM_Z_INDEX,
              }}
              role="alertdialog"
            >
              {children}
            </span>,
            document.body
          )
        : null}
    </span>
  );
};

export default MarketplaceSyncPopconfirm;
